import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { 
  ShoppingCart, Trash2, Plus, Minus, ArrowLeft, Calendar as CalendarIcon, 
  Package, MapPin, Phone, Loader2, CheckCircle, AlertCircle
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import axios from 'axios';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { getErrorMessage } from '../utils/errorHandler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const getImageUrl = (item) => {
  const img = item?.image_url || item?.image;
  if (!img) return 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=100&q=80';
  if (img.startsWith('/api')) return `${BACKEND_URL}${img}`;
  if (img.startsWith('http')) return img;
  return `${BACKEND_URL}${img}`;
};

const Cart = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cartItems, removeFromCart, updateQuantity, clearCart, getCartTotal } = useCart();
  
  // Load Google Maps for address autocomplete
  useGoogleMaps();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [platformFeePercent, setPlatformFeePercent] = useState(10); // Default 10%
  
  // Shared booking details for all items
  const [bookingDetails, setBookingDetails] = useState({
    startDate: null,
    endDate: null,
    location: '',
    contactPhone: user?.phone || '',
    notes: ''
  });

  // Fetch platform settings for dynamic fee
  useEffect(() => {
    const fetchPlatformSettings = async () => {
      try {
        const response = await axios.get(`${API}/platform/settings`);
        setPlatformFeePercent(response.data.platform_fee_percent ?? 10);
      } catch (error) {
        console.error('Failed to fetch platform settings:', error);
      }
    };
    fetchPlatformSettings();
  }, []);

  const duration = bookingDetails.startDate && bookingDetails.endDate 
    ? Math.max(1, differenceInDays(bookingDetails.endDate, bookingDetails.startDate) + 1)
    : 1;

  const subtotal = cartItems.reduce((total, item) => total + (item.price * item.quantity * duration), 0);
  const platformFee = subtotal * (platformFeePercent / 100);
  const totalAmount = subtotal + platformFee;

  const handleCheckout = async () => {
    if (!user) {
      navigate('/login', { state: { from: '/cart' } });
      return;
    }

    if (!bookingDetails.startDate || !bookingDetails.endDate) {
      setError('Please select rental dates');
      return;
    }

    if (!bookingDetails.location.trim()) {
      setError('Please enter delivery location');
      return;
    }

    if (!bookingDetails.contactPhone.trim()) {
      setError('Please enter contact phone number');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Call cart checkout API which handles bookings and PayFast
      const response = await axios.post(`${API}/payments/cart-checkout`, {
        items: cartItems.map(item => ({
          equipment_id: item.equipment_id,
          quantity: item.quantity
        })),
        start_date: format(bookingDetails.startDate, 'yyyy-MM-dd'),
        end_date: format(bookingDetails.endDate, 'yyyy-MM-dd'),
        location: bookingDetails.location,
        contact_phone: bookingDetails.contactPhone,
        notes: bookingDetails.notes
      }, { withCredentials: true });

      if (response.data.success && response.data.payment_url) {
        // Clear cart before redirecting to payment
        clearCart();
        // Redirect to PayFast
        window.location.href = response.data.payment_url;
      } else {
        throw new Error('Failed to initiate payment');
      }

    } catch (err) {
      setError(getErrorMessage(err, 'Failed to process checkout. Please try again.'));
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="bg-gray-800 border-gray-700 max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Booking Successful!</h2>
            <p className="text-gray-400 mb-4">
              Your equipment has been booked. You will receive confirmation shortly.
            </p>
            <Button 
              onClick={() => navigate('/my-bookings')}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              View My Bookings
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen bg-gray-900 p-4 md:p-8">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/equipment')}
            className="text-gray-400 hover:text-white mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Browse Equipment
          </Button>
          
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="pt-12 pb-12 text-center">
              <ShoppingCart className="h-16 w-16 text-gray-500 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2">Your Cart is Empty</h2>
              <p className="text-gray-400 mb-6">
                Add equipment items to your cart to book multiple items at once.
              </p>
              <Button 
                onClick={() => navigate('/equipment')}
                className="bg-yellow-500 hover:bg-yellow-600 text-black"
              >
                <Package className="h-4 w-4 mr-2" />
                Browse Equipment
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <Button 
          variant="ghost" 
          onClick={() => navigate('/equipment')}
          className="text-gray-400 hover:text-white mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Continue Shopping
        </Button>

        <h1 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
          <ShoppingCart className="h-8 w-8 text-yellow-500" />
          Your Cart ({cartItems.length} item{cartItems.length > 1 ? 's' : ''})
        </h1>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map(item => (
              <Card key={item.equipment_id} className="bg-gray-800 border-gray-700">
                <CardContent className="p-4">
                  <div className="flex gap-4">
                    <img 
                      src={getImageUrl(item)} 
                      alt={item.name}
                      className="w-24 h-24 object-cover rounded-lg"
                    />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-white">{item.name}</h3>
                          <p className="text-sm text-gray-400">{item.category}</p>
                          {item.business_name && (
                            <p className="text-xs text-gray-500">by {item.business_name}</p>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFromCart(item.equipment_id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      
                      <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(item.equipment_id, item.quantity - 1)}
                            className="h-8 w-8 p-0 border-gray-600"
                          >
                            <Minus className="h-3 w-3" />
                          </Button>
                          <span className="text-white w-8 text-center">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(item.equipment_id, item.quantity + 1)}
                            disabled={item.quantity >= (item.quantity_available || 10)}
                            className="h-8 w-8 p-0 border-gray-600"
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                        </div>
                        <div className="text-right">
                          <p className="text-yellow-500 font-semibold">
                            R{(item.price * item.quantity * duration).toLocaleString()}
                          </p>
                          <p className="text-xs text-gray-500">
                            R{item.price}/day × {item.quantity} × {duration} day{duration > 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            <Button
              variant="ghost"
              onClick={clearCart}
              className="text-red-400 hover:text-red-300"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear Cart
            </Button>
          </div>

          {/* Booking Details & Summary */}
          <div className="space-y-4">
            {/* Rental Dates */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5 text-yellow-500" />
                  Rental Dates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-gray-300 text-sm">Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left bg-gray-700 border-gray-600 text-white"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {bookingDetails.startDate ? format(bookingDetails.startDate, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-gray-800 border-gray-700">
                      <Calendar
                        mode="single"
                        selected={bookingDetails.startDate}
                        onSelect={(date) => setBookingDetails(prev => ({ ...prev, startDate: date }))}
                        disabled={(date) => date < new Date()}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <div>
                  <Label className="text-gray-300 text-sm">End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left bg-gray-700 border-gray-600 text-white"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {bookingDetails.endDate ? format(bookingDetails.endDate, 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-gray-800 border-gray-700">
                      <Calendar
                        mode="single"
                        selected={bookingDetails.endDate}
                        onSelect={(date) => setBookingDetails(prev => ({ ...prev, endDate: date }))}
                        disabled={(date) => date < (bookingDetails.startDate || new Date())}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {duration > 1 && (
                  <p className="text-sm text-yellow-500">
                    Rental period: {duration} days
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Delivery Details */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-yellow-500" />
                  Delivery Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <AddressAutocomplete
                  label="Delivery Location"
                  value={bookingDetails.location}
                  onChange={(value) => setBookingDetails(prev => ({ ...prev, location: value }))}
                  placeholder="Enter delivery address..."
                  required
                />

                <div>
                  <Label className="text-gray-300 text-sm flex items-center gap-2">
                    <Phone className="h-3 w-3" /> Contact Phone
                  </Label>
                  <Input
                    value={bookingDetails.contactPhone}
                    onChange={(e) => setBookingDetails(prev => ({ ...prev, contactPhone: e.target.value }))}
                    placeholder="+27 XX XXX XXXX"
                    className="bg-gray-700 border-gray-600 text-white mt-1"
                  />
                </div>

                <div>
                  <Label className="text-gray-300 text-sm">Notes (Optional)</Label>
                  <Textarea
                    value={bookingDetails.notes}
                    onChange={(e) => setBookingDetails(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any special instructions..."
                    className="bg-gray-700 border-gray-600 text-white mt-1"
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Order Summary */}
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg text-white">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-gray-400">
                  <span>Subtotal ({cartItems.length} items)</span>
                  <span>R{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Platform Fee ({platformFeePercent}%)</span>
                  <span>R{platformFee.toLocaleString()}</span>
                </div>
                {platformFeePercent === 0 && (
                  <div className="text-green-400 text-xs bg-green-900/20 p-2 rounded">
                    Platform fee waived during promotional period!
                  </div>
                )}
                <div className="border-t border-gray-700 pt-2 mt-2">
                  <div className="flex justify-between text-white font-bold text-lg">
                    <span>Total</span>
                    <span className="text-yellow-500">R{totalAmount.toLocaleString()}</span>
                  </div>
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-400 text-sm bg-red-900/20 p-2 rounded">
                    <AlertCircle className="h-4 w-4" />
                    {error}
                  </div>
                )}

                <Button
                  onClick={handleCheckout}
                  disabled={loading || !bookingDetails.startDate || !bookingDetails.endDate}
                  className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold mt-4"
                  data-testid="checkout-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Proceed to Payment
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
